import React, { useMemo, useState } from 'react';
import Heatmap, { HeatmapLegend } from './Heatmap';
import ViewToggle from './ViewToggle';
import { FadeIn } from './Loading';
import { ThemeManager, ThemeManagerToggle, ThemeSelect } from './ThemeManager';
import { compareByTheme } from './useThemes';
import { positionTiles, unrealizedPct } from './positionTiles';
import { formatPct } from './timeframes';
import { formatPrice, formatQuantity } from './cryptoFormat';
import { useT } from '../../i18n';

// Sortable, filterable positions table.
//
// Columns the brokers populate differently are rendered as an em dash rather
// than hidden: a blank cell says "no value", a missing column says "we never
// asked", and the first is the truth. IBKR gives open dates and country;
// Fidelity via SnapTrade will not.
//
// Cash is a row like any other, tagged (CASH). Fidelity sweeps into SPAXX and
// so carries a real ticker; IBKR reports only a total, so its row is
// synthesised and reads CASH. Neither double-counts — the portfolio total is
// summed from the brokers' own account totals, not from these rows.
//
// The platform filter used to live here. It is the page's now: it selects which
// ACCOUNT the whole view describes, not which rows this one table shows, and
// having both meant the table could disagree with the total above it. What is
// left here is search and sort, which really are the table's own.

const COLUMNS = [
    { key: 'symbol', labelKey: 'positions.symbol', align: 'left' },
    { key: 'name', labelKey: 'positions.name', align: 'left', wide: true },
    // Custom, org-wide, one per asset. Only present when the page passes
    // `themes`; sorting on it groups the rows by theme, biggest first.
    { key: 'theme', labelKey: 'themes.column', align: 'left', themed: true },
    { key: 'accountLabel', labelKey: 'positions.account', align: 'left' },
    { key: 'sector', labelKey: 'positions.sector', align: 'left' },
    { key: 'quantity', labelKey: 'positions.qty', align: 'right', numeric: true },
    { key: 'marketPrice', labelKey: 'positions.price', align: 'right', numeric: true },
    { key: 'marketValueBase', labelKey: 'positions.value', align: 'right', numeric: true },
    { key: 'unrealizedPnlBase', labelKey: 'positions.unrealised', align: 'right', numeric: true, signed: true },
    // Against cost, not current value — the heatmap's colour, as a number.
    { key: 'unrealizedPct', labelKey: 'positions.unrealisedPct', align: 'right', numeric: true, signed: true },
    { key: 'percentOfNav', labelKey: 'positions.pctNav', align: 'right', numeric: true },
    { key: 'openedAt', labelKey: 'positions.opened', align: 'right' },
];

const PLATFORM_LABEL = { ibkr: 'IBKR', fidelity: 'Fidelity', crypto: 'Crypto', private: 'Private' };

/** Enough of the account number to tell two accounts apart, not enough to
 *  quote. Only ever shown when the labels alone cannot. */
const mask = (id) => (!id || id.length <= 5 ? id : `${id.slice(0, 2)}…${id.slice(-4)}`);

/**
 * Names for the Account column.
 *
 * The label comes from the CONNECTION, and two IBKR connections can carry the
 * same one — both of the live ones are called "Interactive Brokers", so the
 * column said the same thing on every IBKR row and answered nothing. Where a
 * label covers more than one account the number disambiguates it; where it
 * does not, the number is noise and stays off the page.
 */
function accountLabels(positions) {
    const idsByLabel = new Map();
    for (const p of positions) {
        const label = p.accountLabel ?? PLATFORM_LABEL[p.source] ?? p.source;
        if (!idsByLabel.has(label)) idsByLabel.set(label, new Set());
        idsByLabel.get(label).add(p.accountId ?? null);
    }
    const names = new Map();
    for (const p of positions) {
        const label = p.accountLabel ?? PLATFORM_LABEL[p.source] ?? p.source;
        const key = `${label}|${p.accountId ?? ''}`;
        names.set(
            key,
            idsByLabel.get(label).size > 1 && p.accountId
                // A wallet is told apart by its chain; its account id is an
                // internal "crypto:7" that means nothing to a reader.
                ? `${label} · ${p.source === 'crypto' ? p.exchange ?? '' : mask(p.accountId)}`
                : label,
        );
    }
    return names;
}

/** Formats a decimal string for display without converting through float
 *  arithmetic — the value arrives correct and only needs grouping. */
function formatNumber(value, { decimals = 2 } = {}) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function cellContent(position, column, baseCurrency, t, td, accountNames) {
    const raw = position[column.key];

    if (column.key === 'symbol') {
        if (!position.isCash) return raw;
        // Tagged rather than renamed: SPAXX is what the statement says, and a
        // reader reconciling against Fidelity needs to see the ticker.
        return (
            <>
                {raw} <span className="positions__cash">{t('positions.cashTag')}</span>
            </>
        );
    }
    if (column.key === 'accountLabel') {
        // The server names the private book in English; the table speaks the
        // page's language.
        if (position.source === 'private') return t('accounts.privateLabel');
        // The account, not the broker. With two accounts at the same broker,
        // "IBKR" on every row says nothing about which book a holding is in.
        const label = raw || PLATFORM_LABEL[position.source] || position.source;
        return accountNames?.get(`${label}|${position.accountId ?? ''}`) ?? label;
    }
    if (column.key === 'sector') return td('sector', raw) || null;
    if (column.key === 'openedAt') return raw || null;
    if (column.key === 'percentOfNav') {
        const n = formatNumber(raw, { decimals: 1 });
        return n === null ? null : `${n}%`;
    }
    if (column.key === 'unrealizedPct') return formatPct(raw, 1);
    // A token is not a share: 0.16 SOL rounded to whole units reads "0", and a
    // $0.0004 price at two decimals reads "free". The Crypto tab's formats.
    if (position.source === 'crypto' && column.key === 'quantity') return formatQuantity(Number(raw));
    if (position.source === 'crypto' && column.key === 'marketPrice') return formatPrice(Number(raw));
    if (column.key === 'quantity') return formatNumber(raw, { decimals: 0 });
    if (column.key === 'marketPrice') {
        const n = formatNumber(raw);
        if (n === null) return null;
        // Price is quoted in the listing currency while Value and Unrealised
        // are converted to base. Tagging the odd ones out is what stops a
        // 262,500 KRW share price from reading as a $262,500 share price.
        const foreign = position.currency && position.currency !== baseCurrency;
        return foreign ? (
            <>
                {n} <span className="positions__ccy">{position.currency}</span>
            </>
        ) : n;
    }
    if (column.numeric) return formatNumber(raw);
    return raw || null;
}

export default function PositionsTable({ positions, baseCurrency, themes = null }) {
    const { t, tn, td } = useT();
    const [view, setView] = useState('table');
    const [managing, setManaging] = useState(false);
    const columns = themes ? COLUMNS : COLUMNS.filter((c) => !c.themed);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: 'marketValueBase', direction: 'desc' });

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        // Derived once here rather than in the cell, so it sorts like any
        // column the broker supplies.
        const enriched = positions.map((p) => ({ ...p, unrealizedPct: unrealizedPct(p) }));
        const filtered = enriched.filter((p) => {
            if (!needle) return true;
            // Searches the descriptive fields only. Matching against numbers
            // would make "2" hit almost every row via quantity or price.
            return [
                p.symbol,
                p.name,
                p.sector,
                td('sector', p.sector),
                p.assetType,
                td('assetType', p.assetType),
                p.exchange,
                p.country,
                p.accountLabel,
                themes?.themeOf(p.symbol)?.name,
                // So "cash" finds the sweep row whatever the broker named the
                // instrument — Fidelity calls it an Open Ended Fund.
                p.isCash ? `cash ${t('positions.cashTag')}` : null,
            ].some((field) => field && String(field).toLowerCase().includes(needle));
        });

        if (sort.key === 'theme' && themes) {
            return [...filtered].sort(compareByTheme(themes.themeOf, sort.direction, 'marketValueBase'));
        }
        const column = COLUMNS.find((c) => c.key === sort.key);
        const sorted = [...filtered].sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            // Rows missing the sort field always sink, whichever direction is
            // active — otherwise ascending order opens with a wall of dashes.
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const cmp = column?.numeric
                ? Number(av) - Number(bv)
                : String(av).localeCompare(String(bv));
            return sort.direction === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [positions, query, sort, themes]);

    const toggleSort = (key) =>
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc' },
        );

    const total = visible.reduce((acc, p) => acc + Number(p.marketValueBase || 0), 0);
    const accountNames = accountLabels(positions);

    return (
        <>
            <div className="positions__controls">
                <div className="positions__filters">
                    <input
                        type="search"
                        className="positions__search"
                        placeholder={t('common.filter')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label={t('positions.filterAria')}
                    />
                    {themes && (
                        <ThemeManagerToggle open={managing} onToggle={() => setManaging((v) => !v)} />
                    )}
                </div>
                <div className="positions__count">
                    <ViewToggle
                        ariaLabel={t('positions.viewAria')}
                        options={[['table', t('common.table')], ['heatmap', t('common.heatmap')]]}
                        value={view}
                        onChange={setView}
                    />
                    {tn('positions.count', visible.length)}
                    {total > 0 && (
                        <span className="positions__subtotal">
                            {' · '}
                            {formatNumber(total)} {baseCurrency}
                        </span>
                    )}
                </div>
            </div>

            {themes && managing && <ThemeManager themes={themes} />}
            {themes && !managing && themes.error && (
                <p className="holdings__error" role="alert">{t(themes.error)}</p>
            )}

            <FadeIn viewKey={view}>
            {view === 'heatmap' ? (
                <>
                    <Heatmap
                        height={520}
                        tiles={positionTiles(visible)}
                        emptyMessage={t('positions.heatmapEmpty')}
                    />
                    <HeatmapLegend label={t('positions.heatmapLegend')} />
                </>
            ) : (
            <div className="positions__scroll">
                <table className="positions">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={`is-${column.align}${column.wide ? ' is-wide' : ''}${
                                        sort.key === column.key ? ' is-sorted' : ''
                                    }`}
                                >
                                    <button type="button" onClick={() => toggleSort(column.key)}>
                                        {t(column.labelKey)}
                                        {sort.key === column.key && (
                                            <span aria-hidden="true">
                                                {sort.direction === 'asc' ? ' ↑' : ' ↓'}
                                            </span>
                                        )}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((position) => (
                            // Keyed on the account ID, not the label. Two IBKR
                            // connections both label their rows "Interactive
                            // Brokers", so a ticker held in both — GLD, URNM —
                            // produced two identical keys, and React reconciled
                            // the pair as one row that changed identity
                            // whenever the list was refiltered.
                            <tr
                                key={`${position.source}-${position.accountId ?? position.accountLabel}-${position.symbol}`}
                            >
                                {columns.map((column) => {
                                    if (column.themed) {
                                        return (
                                            <td key={column.key} className="is-left">
                                                <ThemeSelect themes={themes} symbol={position.symbol} />
                                            </td>
                                        );
                                    }
                                    const content = cellContent(
                                        position, column, baseCurrency, t, td, accountNames,
                                    );
                                    const negative =
                                        column.signed && Number(position[column.key]) < 0;
                                    return (
                                        <td
                                            key={column.key}
                                            className={`is-${column.align}${column.wide ? ' is-wide' : ''}${
                                                negative ? ' is-negative' : ''
                                            }${column.signed && !negative && content ? ' is-positive' : ''}`}
                                        >
                                            {content ?? <span className="positions__empty">—</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
            </FadeIn>
        </>
    );
}
